import clsx from 'clsx';
import { FC, ReactNode } from 'react';

export interface ButtonProps {
  label?: string;
  size?: 'small' | 'medium' | 'large';
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'critical'
    | 'success'
    | 'ghost';
  disabled?: boolean;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  onClick?: () => void;
  className?: string;
}

const getSizeClasses = (size: ButtonProps['size'], isIconOnly: boolean) => {
  if (isIconOnly) {
    switch (size) {
      case 'small':
        return 'text-subtitle2b h-8 p-1.5';
      case 'large':
        return 'text-h6 h-10 p-2.5';
      case 'medium':
      default:
        return 'text-subtitle1b h-[34px]  h-9 p-2';
    }
  }

  switch (size) {
    case 'small':
      return 'text-subtitle2b h-8 px-2.5 py-1.5';
    case 'large':
      return 'text-buttonLarge h-10 px-3.5 py-2.5';
    case 'medium':
    default:
      return 'text-subtitle1b h-[34px] px-3 py-2';
  }
};

const getVariantClasses = (variant: ButtonProps['variant']) => {
  switch (variant) {
    case 'secondary':
      return 'bg-[#768390] hover:bg-[#546474] active:bg-[#43505d] outline-[#43505d] disabled:bg-[#0f253b]';
    case 'tertiary':
      return 'bg-transparent !text-primary border border-[#c0c0c0] hover:bg-[#ebeff2] hover:border-[#ebeff2] active:bg-[#bbc1c7] active:border-[#bbc1c7] outline-[#bbc1c7] disabled:bg-transparent';
    case 'critical':
      return 'bg-red-300 hover:bg-red-400 active:bg-red-500 outline-red-500 disabled:red-300';
    case 'success':
      return 'bg-green-300 hover:bg-green-400 active:bg-green-500 outline-green-500 disabled:green-300';
    case 'ghost':
      return 'bg-transparent text-[#0f253b] hover:text-[#323c46] active:text-[#43505d] outline-none';
    case 'primary':
    default:
      return 'bg-primary hover:bg-[#323c46] active:bg-[#43505d] outline-[#43505d] disabled:bg-[#0f253b]';
  }
};

const Button: FC<ButtonProps> = ({
  label,
  size = 'medium',
  variant = 'primary',
  disabled = false,
  loading = false,
  iconLeft,
  iconRight,
  onClick,
  className,
}) => {
  const isIconOnly = !label && (iconLeft || iconRight);
  const baseClasses = `rounded-md justify-center items-center gap-2
     inline-flex text-center text-white focus:outline outline-2 outline-offset-2 disabled:opacity-40`;
  const sizeClasses = getSizeClasses(size, !!isIconOnly);
  const variantClasses = getVariantClasses(variant);

  return (
    <button
      className={clsx(baseClasses, sizeClasses, variantClasses, className)}
      disabled={disabled || loading}
      type="button"
      onClick={onClick}
    >
      {loading ? (
        <i
          className="ri-loader-2-line animate-spin"
          style={{ color: 'inherit', fontSize: '125%' }}
        />
      ) : (
        <>
          {iconLeft}
          {label}
          {iconRight}
        </>
      )}
    </button>
  );
};

export default Button;
